/**
 * CertLynx Typography System - Professional Workday Style
 * 
 * Clean, professional typography classes inspired by enterprise platforms.
 * Use these classes consistently across all pages and components.
 */

// Main headings (h1) - Large, bold, for page titles
export const heading1 = "text-4xl font-bold tracking-tight text-gray-900";

// Secondary headings (h2) - Medium-large, semibold, for section titles
export const heading2 = "text-3xl font-semibold tracking-tight text-gray-900";

// Tertiary headings (h3) - Medium, semibold, for subsection titles
export const heading3 = "text-2xl font-semibold tracking-tight text-gray-900";

// Quaternary headings (h4) - Small-medium, semibold, for card titles
export const heading4 = "text-xl font-semibold tracking-tight text-gray-900";

// Body text - Standard paragraph text
export const bodyText = "text-base leading-relaxed text-gray-700";

// Large body text - For hero descriptions and important content
export const bodyTextLarge = "text-lg leading-relaxed text-gray-700";

// Muted text - For secondary information, descriptions
export const mutedText = "text-gray-600";

// Small muted text - For captions, metadata
export const mutedTextSmall = "text-sm text-gray-500";

// Form labels - Consistent label styling
export const formLabel = "text-sm font-medium text-gray-700 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70";

// Card titles - For card headers
export const cardTitle = "text-lg font-semibold text-gray-900";

// Card descriptions - For card subtitles
export const cardDescription = "text-sm text-gray-600";

// Badge text - For tags and badges
export const badgeText = "text-xs font-medium";

// Button text - For button labels
export const buttonText = "text-sm font-medium";

// Large button text - For prominent buttons
export const buttonTextLarge = "text-base font-medium";

// Navigation text - For nav items
export const navText = "text-sm font-medium";

// Footer text - For footer content
export const footerText = "text-sm text-gray-500";

// Alert text - For alert messages
export const alertText = "text-sm";

// Alert title - For alert headings
export const alertTitle = "text-lg font-semibold text-gray-900";

// Alert description - For alert descriptions
export const alertDescription = "text-sm text-gray-600";

// Sidebar text - For sidebar navigation
export const sidebarText = "text-sm font-medium text-gray-700";

// Sidebar title - For sidebar section titles
export const sidebarTitle = "text-lg font-semibold text-gray-900";

// Table text - For table content
export const tableText = "text-sm text-gray-900";

// Table header - For table column headers
export const tableHeader = "text-sm font-semibold text-gray-500 uppercase tracking-wider";

// Code text - For code snippets
export const codeText = "text-sm font-mono text-gray-700";

// Quote text - For blockquotes
export const quoteText = "text-lg italic text-gray-700";

// Caption text - For image captions, small notes
export const captionText = "text-xs text-gray-500";

// Link text - For inline links
export const linkText = "text-sm text-blue-600 hover:text-blue-700 hover:underline";

// Error text - For error messages
export const errorText = "text-sm text-red-600";

// Success text - For success messages
export const successText = "text-sm text-green-600";

// Warning text - For warning messages
export const warningText = "text-sm text-yellow-600";

// Info text - For informational messages
export const infoText = "text-sm text-blue-600";

// Professional metric typography (matching the image)
export const metricValue = "text-2xl font-bold text-gray-900";
export const metricLabel = "text-sm font-medium text-gray-600";
export const metricChange = "text-sm font-medium text-gray-500";
export const metricChangePositive = "text-sm font-medium text-green-600";
export const metricChangeNegative = "text-sm font-medium text-red-600";

// Professional navigation typography
export const navItem = "text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors duration-200";
export const navItemActive = "text-sm font-semibold text-white bg-blue-600 px-3 py-2 rounded-md";

// Professional card typography
export const cardTitleLarge = "text-xl font-semibold text-gray-900";
export const cardSubtitle = "text-sm text-gray-600";

// Professional status typography
export const statusText = "text-xs font-medium";
export const statusSuccess = "text-xs font-medium text-green-800";
export const statusWarning = "text-xs font-medium text-yellow-800";
export const statusInfo = "text-xs font-medium text-blue-800";
export const statusNeutral = "text-xs font-medium text-gray-800";

// Professional form typography
export const formLabelLarge = "block text-sm font-medium text-gray-700";
export const formHelp = "text-sm text-gray-500";
export const formError = "text-sm text-red-600";

// Professional data typography
export const dataValue = "text-2xl font-bold text-gray-900";
export const dataLabel = "text-sm font-medium text-gray-600";
export const dataChange = "text-sm font-medium text-gray-500";
export const dataChangePositive = "text-sm font-medium text-green-600";
export const dataChangeNegative = "text-sm font-medium text-red-600";

// Professional banner typography
export const bannerTitle = "text-2xl font-bold text-white";
export const bannerSubtitle = "text-base text-white opacity-90";

// Professional progress typography
export const progressLabel = "text-sm font-medium text-gray-700";
export const progressValue = "text-sm font-semibold text-green-600";
export const progressStatus = "text-xs font-medium text-gray-600";

// Professional activity typography
export const activityTitle = "text-sm font-medium text-gray-900";
export const activityTime = "text-xs text-gray-500";
export const activityStatus = "text-xs font-medium";

// Professional group typography
export const groupTitle = "text-base font-semibold text-gray-900";
export const groupMeta = "text-sm text-gray-600";
export const groupStatus = "text-xs font-medium";
export const groupNextSession = "text-sm font-semibold text-blue-600";

// Professional tutor typography
export const tutorName = "text-base font-semibold text-gray-900";
export const tutorDetails = "text-sm text-gray-600";
export const tutorAvailability = "text-sm font-medium text-green-600";
export const tutorSpecialty = "text-xs font-medium";

// Professional forum typography
export const forumTitle = "text-base font-semibold text-gray-900";
export const forumDescription = "text-sm text-gray-600";
export const forumStats = "text-xs text-gray-500";
export const forumTags = "text-xs font-medium"; 